/**
 * Shared API Client
 *
 * Centralizes all API communication with the Supabase Edge Function server.
 * All API utility files should import from here instead of defining their own
 * fetch helpers.
 *
 * SECURITY: All fetch helpers automatically include the session token when
 * available, so server-side requireAuth middleware can validate the user.
 *
 * 401 handling: when the server responds 401 AND we had sent a session token
 * (meaning the token was rejected as invalid/expired), we trigger a global
 * logout via authBridge. If no token was sent (e.g. new tab without session),
 * we do NOT trigger logout — the page/component should handle the error
 * gracefully instead of force-logging out the user.
 *
 * RETRY LOGIC: apiFetch and apiRawFetch now include automatic retry with
 * exponential backoff for network errors and 5xx server failures. Retry is
 * disabled for 4xx errors and can be customized or turned off per-call via
 * the `retry` option in ApiFetchOptions.
 *
 * Exports:
 *   - API_BASE            — base URL for all API calls
 *   - apiFetch(path, opt) — standard fetch with anon key + session token + retry
 *   - authFetch(path, opt)— alias for apiFetch (backward compat)
 *   - apiRawFetch(path, opt) — raw fetch (no JSON parsing) + retry
 *   - getSessionTokenValue() — get current session token (for keepalive/direct fetches)
 */

import { projectId, publicAnonKey } from "../../../utils/supabase/info";
import { triggerGlobalLogout } from "./authBridge";
import { retryFetch, type RetryOptions } from "./retryFetch";

/** Base URL for all server API calls */
export const API_BASE = `https://${projectId}.supabase.co/functions/v1/server`;

/** Session token storage key — must match userApi.ts */
const SESSION_TOKEN_KEY = "zap_session_token";

/** Get current session token (exported for use in keepalive/direct fetches) */
export function getSessionTokenValue(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Build standard headers with auth + optional session token */
function buildHeaders(extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${publicAnonKey}`,
    ...((extra as Record<string, string>) || {}),
  };

  const token = getSessionTokenValue();
  if (token) {
    headers["X-Session-Token"] = token;
  }

  return headers;
}

/**
 * Decide whether a 401 response should trigger a forced global logout.
 *
 * Rules:
 *   1. Never logout for /auth/* paths (login, validate, etc.)
 *   2. Only logout if we actually sent a session token — if no token was
 *      present, the 401 is expected (user hasn't logged in yet, or opened
 *      a new tab without sessionStorage). Force-logging out in this case
 *      would create annoying redirect loops.
 *   3. Skip logout for admin-only paths — a 401 there is typically a
 *      session-timing issue, not a reason to nuke the entire session.
 *      The admin page itself will show an error message.
 */
const ADMIN_ONLY_PREFIXES = [
  "/debug/",
  "/test-email",
  "/clientboard-",
  "/settings",
  "/profiles/",
  "/vault/",
  "/feature-flags",
  "/audit-log",
];

function shouldForceLogout(path: string, hadToken: boolean): boolean {
  // Never for auth endpoints
  if (path.startsWith("/auth/")) return false;
  // Only if we actually sent a token that was rejected
  if (!hadToken) return false;
  // Skip for admin-only endpoints — let the page handle the error
  for (const prefix of ADMIN_ONLY_PREFIXES) {
    if (path.startsWith(prefix)) return false;
  }
  return true;
}

/* ================================================================
   Extended options type — adds `retry` to standard RequestInit
   ================================================================ */

export interface ApiFetchOptions extends RequestInit {
  /** Retry configuration. Pass `{ noRetry: true }` to disable. */
  retry?: RetryOptions;
}

/**
 * Standard API fetch — sends the Supabase anon key + session token (when available).
 * Used for all endpoints. The session token allows server-side auth validation.
 *
 * Now includes automatic retry with exponential backoff for network errors and
 * 5xx server errors. Customize or disable via the `retry` option.
 */
export async function apiFetch(path: string, options: ApiFetchOptions = {}): Promise<any> {
  const { retry: retryOpts, ...fetchOpts } = options;
  const hadToken = !!getSessionTokenValue();

  const res = await retryFetch(
    () =>
      fetch(`${API_BASE}${path}`, {
        ...fetchOpts,
        headers: buildHeaders(fetchOpts.headers),
      }),
    retryOpts,
  );

  if (!res.ok) {
    const errBody = await res.text();
    // On 401: only force logout if we actually sent a token that was rejected
    // and the path is not an admin-only endpoint (see shouldForceLogout).
    if (res.status === 401 && shouldForceLogout(path, hadToken)) {
      console.warn(`[Auth] Forced logout triggered by 401 on ${path}`);
      triggerGlobalLogout();
    }
    // On 429: rate limited — show user-friendly toast
    if (res.status === 429) {
      try {
        const { toast } = await import("sonner");
        toast.error("\u05D9\u05D5\u05EA\u05E8 \u05DE\u05D3\u05D9 \u05D1\u05E7\u05E9\u05D5\u05EA. \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1 \u05E2\u05D5\u05D3 \u05E8\u05D2\u05E2.", { duration: 5000 });
      } catch { /* sonner may not be available */ }
    }
    throw new Error(`API error ${res.status}: ${errBody}`);
  }
  return res.json();
}

/**
 * Authenticated API fetch — identical to apiFetch (kept for backward compat).
 * Both now always include the session token when available.
 */
export async function authFetch(path: string, options: ApiFetchOptions = {}): Promise<any> {
  return apiFetch(path, options);
}

/**
 * Raw fetch (no JSON parsing) — for endpoints that may return non-JSON responses.
 * Also includes session token and retry logic.
 */
export async function apiRawFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { retry: retryOpts, ...fetchOpts } = options;
  const hadToken = !!getSessionTokenValue();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
    ...((fetchOpts.headers as Record<string, string>) || {}),
  };

  const token = getSessionTokenValue();
  if (token) {
    headers["X-Session-Token"] = token;
  }

  const res = await retryFetch(
    () =>
      fetch(`${API_BASE}${path}`, {
        ...fetchOpts,
        headers,
      }),
    retryOpts,
  );

  if (!res.ok) {
    const errBody = await res.text();
    if (res.status === 401 && shouldForceLogout(path, hadToken)) {
      console.warn(`[Auth] Forced logout triggered by 401 on ${path}`);
      triggerGlobalLogout();
    }
    // On 429: rate limited — show user-friendly toast
    if (res.status === 429) {
      try {
        const { toast } = await import("sonner");
        toast.error("\u05D9\u05D5\u05EA\u05E8 \u05DE\u05D3\u05D9 \u05D1\u05E7\u05E9\u05D5\u05EA. \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1 \u05E2\u05D5\u05D3 \u05E8\u05D2\u05E2.", { duration: 5000 });
      } catch { /* sonner may not be available */ }
    }
    throw new Error(`API error ${res.status}: ${errBody}`);
  }
  return res;
}